import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { products } from '../data/products';

export function HeroProduct() {
  const ref = useRef(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const loadedCount = useRef(0);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  
  const handleImageLoad = () => {
    loadedCount.current += 1;
    // We wait for 3 product images
    if (loadedCount.current >= 3) {
      setImagesLoaded(true);
    }
  };

  // Fallback timer just in case images fail or take too long
  useEffect(() => {
    const timer = setTimeout(() => {
      setImagesLoaded(true);
    }, 2000); // Force show after 2s max
    return () => clearTimeout(timer);
  }, []);
  
  // Scroll-based transforms - ACTIVE ON ALL DEVICES
  // Optimization: We keep the animation logic but ensure GPU acceleration via styles
  
  const textY = useTransform(scrollYProgress, [0, 0.5], ["0%", "-30%"]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.3, 0.5], [1, 0.8, 0]);
  
  // Vitra stays and scales up
  const vitraScale = useTransform(scrollYProgress, [0, 0.4], [1, 1.15]);
  const vitraY = useTransform(scrollYProgress, [0, 0.5], ["0%", "-10%"]);
  
  // Rush and Synaptiq move and fade
  const rushX = useTransform(scrollYProgress, [0, 0.4], ["0%", "15%"]);
  const rushScale = useTransform(scrollYProgress, [0, 0.4], [1, 0.85]);
  const rushOpacity = useTransform(scrollYProgress, [0, 0.2, 0.4], [1, 0.8, 0]);
  
  const synaptiqX = useTransform(scrollYProgress, [0, 0.4], ["0%", "-15%"]);
  const synaptiqScale = useTransform(scrollYProgress, [0, 0.4], [1, 0.85]);
  const synaptiqOpacity = useTransform(scrollYProgress, [0, 0.2, 0.4], [1, 0.8, 0]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden bg-gradient-to-b from-berleto-offwhite via-berleto-offwhite to-white pt-24">
      {/* Background Image fallback */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
        style={{ 
          backgroundImage: 'url(/backgrounds/berletobackground2-optimized.jpg)',
          transform: 'translateZ(0)', // GPU acceleration for background
          willChange: 'opacity'
        }}
      />

      {/* Subtle animated mountain atmosphere */}
      {/* Performance: 19MB H.264 video — hidden on mobile to save bandwidth & CPU */}
      <video
        className="hidden md:block absolute inset-0 w-full h-full object-cover opacity-70"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster="/backgrounds/berletobackground2-optimized.jpg"
        fetchPriority="low"
        disablePictureInPicture
        disableRemotePlayback
        aria-hidden="true"
      >
        <source src="/backgrounds/berleto-mountain-cloud-loop.mp4" type="video/mp4" />
      </video>

      {/* Mobile fallback: lightweight static poster (35 KB vs 19 MB) */}
      <img
        src="/backgrounds/berletobackground2-optimized.jpg"
        alt=""
        className="block md:hidden absolute inset-0 w-full h-full object-cover opacity-70"
        loading="eager"
        aria-hidden="true"
      />
      
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#FAF9F6]/60 via-[#FAF9F6]/40 to-[#FAF9F6]/80" />
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-berleto-sage/5 to-berleto-rust/5 opacity-30" />
      
      {/* Main Container */}
      <div className="relative min-h-screen flex flex-col items-center justify-start px-4 sm:px-6 lg:px-8 pt-24">
        
        {/* Hero Text */}
        <motion.div 
          className="relative z-20 text-center max-w-5xl mx-auto mb-4 mt-0"
          style={{ 
            y: textY, 
            opacity: textOpacity,
            transform: "translateZ(0)",
            willChange: 'transform, opacity'
          }}
        >
          <motion.h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-berleto-warmgray leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={imagesLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >            <span className="block">Elevated</span>
            <span className="block font-medium bg-gradient-to-r from-berleto-rust via-berleto-sage to-berleto-rust bg-clip-text text-transparent">
              Focus
            </span>
            <span className="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-2 text-berleto-sage/80">
              Grounded in Nature
            </span>
          </motion.h1>
          
          <motion.p 
            className="mt-4 text-base sm:text-lg md:text-xl text-berleto-warmgray/70 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={imagesLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
          >
            Premium adaptogenic mushrooms crafted for mental clarity, 
            sustained energy, and optimal performance.
          </motion.p>
          
          <motion.div 
            className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={imagesLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
          >
            <button
              className="min-w-[180px] px-8 py-3 text-sm font-medium tracking-wide transition-colors"
              style={{ background: '#1A2A1A', color: '#FAF9F6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#243424'}
              onMouseLeave={e => e.currentTarget.style.background = '#1A2A1A'}
              onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Explore Collection
            </button>
            <button
              className="min-w-[180px] px-8 py-3 text-sm font-medium tracking-wide border transition-colors"
              style={{ border: '1px solid rgba(26,42,26,0.2)', color: '#1A2A1A', background: 'transparent' }}
              onMouseEnter={e => e.currentTarget.style.background = '#E8DCC4'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              onClick={() => document.getElementById('science')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Learn More
            </button>
          </motion.div>
        </motion.div>

        {/* Product Bottles Container */}
        <div className="relative w-full max-w-6xl mx-auto -mt-12">
          <div className="relative h-[180px] sm:h-[220px] md:h-[280px] lg:h-[320px]">
            
            {/* Rush Bottle - Left Behind */}
            <motion.div
              className="absolute left-[15%] sm:left-[32%] top-[38%] sm:top-[40%] -translate-x-1/2 -translate-y-1/2 z-5"
              style={{ 
                x: rushX,
                scale: rushScale,
                opacity: rushOpacity,
                transform: "translateZ(0)", // GPU acceleration
                willChange: "transform" // Critical for mobile performance
              }}
              initial={{ opacity: 0, x: -100, scale: 0.8 }}
              animate={imagesLoaded ? { opacity: 1, x: 0, scale: 1 } : { opacity: 0, x: -100, scale: 0.8 }}
              transition={{ duration: 1.2, delay: 0.6, ease: "easeOut" }}
            >
                <img
                  src={products.rush.image}
                  alt={products.rush.title}
                  onLoad={handleImageLoad}
                  className="w-[120px] sm:w-[110px] md:w-[140px] lg:w-[170px] opacity-90 drop-shadow-xl hover:drop-shadow-2xl transition-all duration-300 transform-gpu"
                  loading="eager"
                />
            </motion.div>

            {/* Vitra Bottle - Center (Hero) */}
            <motion.div
              className="absolute left-[31%] sm:left-[40%] top-[55%] sm:top-1/2 -translate-x-1/2 -translate-y-1/2 z-30"
              style={{ 
                y: vitraY,
                scale: vitraScale,
                transform: "translateZ(0)", // GPU acceleration
                willChange: "transform" // Critical for mobile performance
              }}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={imagesLoaded ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 50, scale: 0.9 }}
              transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
            >
                <img
                  src={products.vitra.image}
                  alt={products.vitra.title}
                  onLoad={handleImageLoad}
                  className="w-[170px] sm:w-[160px] md:w-[210px] lg:w-[260px] drop-shadow-3xl hover:drop-shadow-4xl transition-all duration-300 transform-gpu"
                  loading="eager"
                />
            </motion.div>
            {/* Synaptiq Bottle - Right Behind */}
            <motion.div
              className="absolute left-[58%] sm:left-[55%] top-[38%] sm:top-[40%] -translate-x-1/2 -translate-y-1/2 z-5"
              style={{ 
                x: synaptiqX,
                scale: synaptiqScale,
                opacity: synaptiqOpacity,
                transform: "translateZ(0)", // GPU acceleration
                willChange: "transform" // Critical for mobile performance
              }}
              initial={{ opacity: 0, x: 100, scale: 0.8 }}
              animate={imagesLoaded ? { opacity: 1, x: 0, scale: 1 } : { opacity: 0, x: 100, scale: 0.8 }}
              transition={{ duration: 1.2, delay: 0.6, ease: "easeOut" }}
            >
                <img
                  src={products.synaptiq.image}
                  alt={products.synaptiq.title}
                  onLoad={handleImageLoad}
                  className="w-[120px] sm:w-[110px] md:w-[140px] lg:w-[170px] opacity-90 drop-shadow-xl hover:drop-shadow-2xl transition-all duration-300 transform-gpu"
                  loading="eager"
                />
            </motion.div>
          </div>
        </div>
        
        {/* Floating Elements / Decorative - HIDDEN ON MOBILE to save performance */}
        {/* These blur effects are very expensive on iOS during scroll */}
        <div className="absolute inset-0 pointer-events-none hidden sm:block">
          <div className="absolute top-20 left-10 w-20 h-20 bg-berleto-rust/10 rounded-full blur-3xl transform-gpu" />
          <div className="absolute bottom-20 right-10 w-32 h-32 bg-berleto-sage/10 rounded-full blur-3xl transform-gpu" />
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0, y: -20 }}
          animate={imagesLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ delay: 2, duration: 1 }}
        >          <motion.div
            className="w-6 h-10 border-2 border-berleto-warmgray/30 rounded-full flex justify-center"
            animate={{ y: [0, 5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.div
              className="w-1 h-3 bg-berleto-warmgray/50 rounded-full mt-2"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
